import 'isomorphic-fetch';
import React from 'react'
import ReactDOM from 'react-dom';
import axios from 'axios';
import {flashMessage} from 'redux-flash';
import {Provider} from 'react-redux';
import {Router, Route, Redirect} from 'react-router';
import {syncHistoryWithStore} from 'react-router-redux';
import {persistStore} from 'redux-persist';
import {createHashHistory, useBasename} from 'history';
import {loadTranslations, syncTranslationWithStore, setLocale} from 'react-redux-i18n';
import {isLogged} from './helpers/Helpers';
import {setLanguages} from './store/actions/language';
import {onLanguageChange} from '../store/actions/global';
import {PersistGate} from 'redux-persist/es/integration/react';
import translations from './config/translations';
import Config from './config';
import asyncComponent from './AsyncComponent';
import {isAdmin, isLocalHost, updateQueryStringParameter} from "../helpers/CoreHelpers";
import {Unauthorized} from '../admin/components/UI/Unauthorized';
import ErrorBoundary from './ErrorBoundary';
import { MediaContextProvider } from '../contexts/MediaContext';

/**
 * Store
 */
import store from "./store";

/**
 * SSO type
 */
const isRedirectSso = isLocalHost() || /.*Version.*Safari.*/.test(window.navigator.userAgent);

/**
 * Polyfill
 */
if (!global._babelPolyfill) {
    require('babel-polyfill');
}

/**
 * Async components: Backend Layout
 */
const MainLayout = asyncComponent(() => import('./components/Layouts/MainLayout'));
const FrontLayout = asyncComponent(() => import('../../frontend/components/Layouts/FrontLayout'));
const Preview = asyncComponent(() => import('../../frontend/pages/Preview'));
const LoginLayout = asyncComponent(() => isRedirectSso ? import('./components/Layouts/LocalLoginLayout') : import('./components/Layouts/LoginLayout'));

/**
 * Async components: Backend
 */
const HomeBackend = asyncComponent(() => import('./pages/Home'));
const WorkspaceBackend = asyncComponent(() => import('./pages/Workspace'));
const AddWorkspaceBackend = asyncComponent(() => import('./pages/AddWorkspace'));
const MediaBackend = asyncComponent(() => import('./pages/Media'));
const WidgetsBackend = asyncComponent(() => import('./pages/Widgets'));
const AddWidgetsBackend = asyncComponent(() => import('./pages/AddWidgets'));
const MenuBackend = asyncComponent(() => import('./pages/Menu'));
const PageBackend = asyncComponent(() => import('./pages/Page'));
const BlockBackend = asyncComponent(() => import('./pages/Block'));
const AddPageBackend = asyncComponent(() => import('./pages/AddPage'));
const AddBlockBackend = asyncComponent(() => import('./pages/AddBlock'));
const AddMenuBackend = asyncComponent(() => import('./pages/AddMenu'));
const AddGlobalFieldBackend = asyncComponent(() => import('./pages/AddGlobalField'));
const GlobalFieldBackend = asyncComponent(() => import('./pages/GlobalField'));
const GuidelinesBackend = asyncComponent(() => import('./pages/Guidelines'));
const AddFormBackend = asyncComponent(() => import('./pages/AddForm'));
const FormEntriesBackend = asyncComponent(() => import('./pages/FormEntries'));
const LanguageOverviewBackend = asyncComponent(() => import('./pages/LanguageOverview'));

/**
 * Settings
 */
const authorizationIsOn = Config.authorization;
const client = document.getElementById('application').getAttribute('data-client');
const initialRoute = document.getElementById('application').getAttribute('data-initial-route');

/**
 * Init
 */
let initialLoad = true;

/**
 * Language sync
 */
syncTranslationWithStore(store);
store.dispatch(loadTranslations(translations));
store.dispatch(setLocale(Config.language));
store.dispatch(setLanguages());

/**
 * Configure store
 */
const gate = persistStore(store);
const basename = window.location.pathname + window.location.search;
const browserHistory = useBasename(createHashHistory)({
    basename: basename === '/' ? '' : ''
});

const history = syncHistoryWithStore(browserHistory, store);

/**
 * Configure axios
 */
axios.interceptors.response.use((response) => {
    return response;
}, function (error) {
    if (Config.getToken()) {
        store.dispatch(
            flashMessage('Something went wrong. Please try again!', {
                timeout: 0,
                props: {
                    type: 'error'
                }
            })
        );
    }

    return Promise.reject(error);
});

/**
 * Log out version
 */
console.log('version: ' + Config.version);

if (document.currentScript) {
    console.log(`Loaded from: ` + document.currentScript.src);
}

/**
 * Auth redirected
 */
let isLoggedIn = isLogged();

/**
 * Clear storage
 */
if (isLoggedIn && initialLoad && authorizationIsOn) {
    Config.clearStorage();
}

/**
 * Wait for iframe change status
 */
store.subscribe(() => {
    let previousValue = isLoggedIn;

    isLoggedIn = store.getState().user.isLoggedIn;

    if (!previousValue && isLoggedIn) {
        if (isRedirectSso) {
            if (window.location.hash.includes('?token=')) {
                history.push(updateQueryStringParameter(window.location.hash, 'token', Config.getToken()).replace('#', ''));
            } else {
                history.push(window.location.hash.replace('#', '') + '?token=' + Config.getToken());
            }
        } else {
            if (window.location.hash.includes('?token=')) {
                window.parent.location.replace(updateQueryStringParameter(window.location.href, 'token', Config.getToken()));
            } else {
                window.parent.location.replace(window.location.href + '?token=' + Config.getToken());
            }
        }
    }
});

/**
 * With backend layout
 */
const withBackendLayout = (WrappedComponent) => {
    return class WithAuthorization extends React.Component {
        componentDidMount() {
            /**
             * Set classes
             */
            document.body.classList.remove('front-page');
            document.body.classList.add('admin-page');
        }

        componentWillMount() {
            const {location: {pathname}} = this.props;

            if (initialLoad && initialRoute) {
                if (initialRoute !== pathname && pathname === '/') {
                    if (isLocalHost()) {
                        console.log('e');
                        window.location.replace(window.location.href + "guidelines");
                    } else {
                        console.log('f');
                        window.location.replace(window.location.href + initialRoute);
                    }
                    initialLoad = false;
                }
            }
        }

        componentDidUpdate(prevProps, prevState, snapshot) {
            if (this.props.params.language !== prevProps.params.language) {
                store.dispatch(onLanguageChange(this.props.routeParams));
            }
        }

        render() {
            if (authorizationIsOn && isLogged() && !isAdmin()) {
                return (
                    <Unauthorized/>
                );
            }

            if (!isLogged() && authorizationIsOn) {
                return (
                    <LoginLayout {...this.props}/>
                );
            } else {
                return (
                    <MainLayout {...this.props}>
                        <WrappedComponent {...this.props} />
                    </MainLayout>
                );
            }
        }
    }
};

/**
 * With Frontend layout
 */
const withFrontendLayout = (WrappedComponent, frontendTheme, pageId) => {
    return class WithAuthorization extends React.Component {
        componentDidMount() {
            document.body.classList.remove('admin-page');
            document.body.classList.add('front-page');

            if (frontendTheme) {
                document.body.classList.add(frontendTheme);
            }
        }

        componentWillMount() {
            const {location: {pathname}} = this.props;

            if (initialLoad && initialRoute) {
                if (initialRoute !== pathname && pathname === '/') {
                    window.location.replace(window.location.href + initialRoute);

                    initialLoad = false;
                }
            }
        }

        render() {
            let props = this.props;

            if (pageId) {
                props.params.page = pageId;
            }

            if (authorizationIsOn && isLogged() && !isAdmin()) {
                return (
                    <Unauthorized/>
                );
            }

            if (!isLogged() && authorizationIsOn) {
                return (
                    <FrontLayout {...props} frontendTheme={frontendTheme}>
                        <LoginLayout {...this.props}/>
                    </FrontLayout>
                )
            } else {
                if (WrappedComponent) {
                    return (
                        <FrontLayout {...props} frontendTheme={frontendTheme}>
                            <WrappedComponent {...props} frontendTheme={frontendTheme}/>
                        </FrontLayout>
                    )
                } else {
                    return (
                        <FrontLayout {...props} frontendTheme={frontendTheme}/>
                    );
                }
            }
        }
    }
};

/**
 * Routes
 */
const routes = [
    {
        path: '/',
        key: 'index',
        exact: true,
        component: withBackendLayout(HomeBackend),
        subMenu: false
    },
    {
        path: '/home',
        key: 'home',
        exact: true,
        component: withBackendLayout(HomeBackend),
        subMenu: false
    },
    {
        path: '/workspace/add',
        key: 'workspace-add',
        exact: true,
        component: withBackendLayout(AddWorkspaceBackend),
        subMenu: false
    },
    {
        path: '/workspace/:workspace',
        key: 'workspace',
        exact: true,
        component: withBackendLayout(WorkspaceBackend),
        subMenu: false
    },
    {
        path: '/workspace/:workspace/pages',
        key: 'workspace-pages',
        exact: true,
        component: withBackendLayout(AddPageBackend),
        subMenu: 'pages'
    },
    {
        path: '/workspace/:workspace/blocks',
        key: 'workspace-blocks',
        exact: true,
        component: withBackendLayout(AddBlockBackend),
        subMenu: 'blocks'
    },
    {
        path: '/workspace/:workspace/menus',
        key: 'workspace-menus',
        exact: true,
        component: withBackendLayout(AddMenuBackend),
        subMenu: 'menus'
    },
    {
        path: '/workspace/:workspace/page/add',
        key: 'page-add',
        exact: true,
        component: withBackendLayout(AddPageBackend),
        subMenu: 'pages'
    },
    {
        path: '/workspace/:workspace/page/:page',
        key: 'page',
        exact: true,
        component: withBackendLayout(PageBackend),
        subMenu: 'pages'
    },
    {
        path: '/workspace/:workspace/block/add',
        key: 'block-add',
        exact: true,
        component: withBackendLayout(AddBlockBackend),
        subMenu: 'blocks'
    },
    {
        path: '/workspace/:workspace/block/:block',
        key: 'block',
        exact: true,
        component: withBackendLayout(BlockBackend),
        subMenu: 'blocks'
    },
    {
        path: '/workspace/:workspace/menu/add',
        key: 'menu-add',
        exact: true,
        component: withBackendLayout(AddMenuBackend),
        subMenu: 'menus'
    },
    {
        path: '/workspace/:workspace/menu/:menu',
        key: 'menu',
        exact: true,
        component: withBackendLayout(MenuBackend),
        subMenu: 'menus'
    },
    {
        path: '/global-field',
        key: 'global-fields',
        exact: true,
        component: withBackendLayout(AddGlobalFieldBackend),
        subMenu: 'global-fields'
    },
    {
        path: '/global-field/add',
        key: 'global-field-add',
        exact: true,
        component: withBackendLayout(AddGlobalFieldBackend),
        subMenu: 'global-fields'
    },
    {
        path: '/global-field/:key',
        key: 'global-field',
        exact: true,
        component: withBackendLayout(GlobalFieldBackend),
        subMenu: 'global-fields'
    },
    {
        path: '/media',
        key: 'media',
        exact: true,
        component: withBackendLayout(MediaBackend),
        subMenu: false
    },
    {
        path: '/widgets',
        key: 'widgets',
        exact: true,
        component: withBackendLayout(AddWidgetsBackend),
        subMenu: 'widgets'
    },
    {
        path: '/widgets/add',
        key: 'widget-add',
        exact: true,
        component: withBackendLayout(AddWidgetsBackend),
        subMenu: 'widgets'
    },
    {
        path: '/widgets/:widget',
        key: 'widget',
        exact: true,
        component: withBackendLayout(WidgetsBackend),
        subMenu: 'widgets'
    },
    {
        path: '/guidelines',
        key: 'guidelines',
        exact: true,
        component: withBackendLayout(GuidelinesBackend),
        subMenu: false
    },
    {
        path: '/forms',
        key: 'forms',
        exact: true,
        component: withBackendLayout(AddFormBackend),
        subMenu: 'forms'
    },
    {
        path: '/forms/:form',
        key: 'form',
        exact: true,
        component: withBackendLayout(FormEntriesBackend),
        subMenu: 'forms'
    },
    {
        path: '/languageOverview',
        key: 'languageOverview',
        exact: true,
        component: withBackendLayout(LanguageOverviewBackend),
        subMenu: false
    },
    {
        path: '/preview',
        key: 'preview',
        exact: true,
        component: withFrontendLayout(Preview, client),
        subMenu: false
    }
];

ReactDOM.render(
    <ErrorBoundary>
        <Provider store={store}>
            <MediaContextProvider>
                <PersistGate persistor={gate}>
                    <Router history={history}>
                        <div>
                            {routes.map(route => <Route {...route} />)}
                            <Redirect from="*" to="/" />
                        </div>
                    </Router>
                </PersistGate>
            </MediaContextProvider>
        </Provider>
    </ErrorBoundary>,
    document.getElementById('application')
);

export {
    basename,
    store,
    browserHistory,
    history,
    routes
};



// WEBPACK FOOTER //
// ./src/core/admin/index.js